/** One picked file, with the path it should take relative to the destination. */
export interface UploadEntry {
  file: File;
  relativePath: string;
}

/**
 * What the user picked for one upload. `folderPaths` carries every folder in
 * the picked tree, so a folder holding no file still reaches the vault.
 */
export interface UploadSelection {
  files: UploadEntry[];
  folderPaths: string[];
  readFailures?: { relativePath: string; error: unknown }[];
}

/**
 * Reads a drop into files and folder paths, walking dropped folders to their
 * leaves. Call this before awaiting anything else in the drop handler: the
 * browser empties `items` once the handler returns. `onFileRead` reports the
 * running file count as the walk progresses.
 */
export async function readDroppedEntries(
  items: DataTransferItemList,
  onFileRead?: (fileCount: number) => void,
): Promise<UploadSelection> {
  const entries = Array.from(items)
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null);
  const countFile = countFilesRead(onFileRead);

  return mergeSelections(
    await Promise.all(entries.map((entry) => readEntry(entry, countFile))),
  );
}

/**
 * Opens the browser's directory picker. Only Chromium browsers expose the
 * picker, so callers check `supportsDirectoryPicker` first and fall back to a
 * folder input. Returns null when the person dismisses the picker, and also
 * when Chrome refuses the folder itself — it refuses the Desktop, Documents,
 * Downloads, and home folders, and reports that refusal as the same abort.
 */
export async function pickDirectory(): Promise<FileSystemDirectoryHandle | null> {
  const directory = await window
    .showDirectoryPicker?.({ mode: "read" })
    .catch((error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError") {
        return null;
      }
      throw error;
    });

  return directory ?? null;
}

/**
 * Reads a picked folder into a selection, empty subfolders included.
 * `onFileRead` reports the running file count as the walk progresses.
 */
export function readPickedDirectory(
  directory: FileSystemDirectoryHandle,
  onFileRead?: (fileCount: number) => void,
): Promise<UploadSelection> {
  return readDirectoryHandle(
    directory,
    directory.name,
    countFilesRead(onFileRead),
  );
}

/** Whether this browser can report the empty subfolders of a picked folder. */
export function supportsDirectoryPicker(): boolean {
  return typeof window.showDirectoryPicker === "function";
}

/**
 * Builds a selection from a file input. Only a folder input sets
 * `webkitRelativePath`, and it reports the files of a picked folder but never
 * its empty subfolders, so no folder path is added.
 */
export function buildInputSelection(selectedFiles: FileList): UploadSelection {
  return {
    files: Array.from(selectedFiles)
      .map((file) => ({
        file,
        relativePath: file.webkitRelativePath || file.name,
      }))
      .filter(({ relativePath }) => !isMetadataPath(relativePath)),
    folderPaths: [],
  };
}

/** Counts the files a walker reads, so both walkers report the same running total. */
function countFilesRead(onFileRead?: (fileCount: number) => void): () => void {
  let fileCount = 0;
  return () => {
    fileCount += 1;
    onFileRead?.(fileCount);
  };
}

async function readDirectoryHandle(
  directory: FileSystemDirectoryHandle,
  relativePath: string,
  countFile: () => void,
): Promise<UploadSelection> {
  const children: Array<Promise<UploadSelection>> = [];
  const readFailures: NonNullable<UploadSelection["readFailures"]> = [];
  try {
    for await (const [name, handle] of directory) {
      children.push(readHandle(handle, `${relativePath}/${name}`, countFile));
    }
  } catch (error) {
    readFailures.push({ relativePath, error });
  }

  return mergeSelections([
    {
      files: [],
      folderPaths: readFailures.length ? [] : [relativePath],
      readFailures,
    },
    ...(await Promise.all(children)),
  ]);
}

async function readHandle(
  handle: FileSystemHandle,
  relativePath: string,
  countFile: () => void,
): Promise<UploadSelection> {
  if (isMetadataPath(relativePath)) {
    return { files: [], folderPaths: [] };
  }

  if (isDirectoryHandle(handle)) {
    return readDirectoryHandle(handle, relativePath, countFile);
  }

  if (!isFileHandle(handle)) {
    return { files: [], folderPaths: [] };
  }

  try {
    const file = await handle.getFile();
    countFile();
    return { files: [{ file, relativePath }], folderPaths: [] };
  } catch (error) {
    return failedSelection(error, relativePath);
  }
}

function isDirectoryHandle(
  handle: FileSystemHandle,
): handle is FileSystemDirectoryHandle {
  return handle.kind === "directory";
}

function isFileHandle(
  handle: FileSystemHandle,
): handle is FileSystemFileHandle {
  return handle.kind === "file";
}

async function readEntry(
  entry: FileSystemEntry,
  countFile: () => void,
): Promise<UploadSelection> {
  const relativePath = entry.fullPath.replace(/^\//, "");

  if (isMetadataPath(relativePath)) {
    return { files: [], folderPaths: [] };
  }

  try {
    if (isFileEntry(entry)) {
      const file = await readFile(entry);
      countFile();
      return { files: [{ file, relativePath }], folderPaths: [] };
    }

    if (!isDirectoryEntry(entry)) {
      return { files: [], folderPaths: [] };
    }

    const readFailures: NonNullable<UploadSelection["readFailures"]> = [];
    const children = await readDirectoryEntries(entry, (error) => {
      readFailures.push({ relativePath, error });
    });
    return mergeSelections([
      {
        files: [],
        folderPaths: readFailures.length ? [] : [relativePath],
        readFailures,
      },
      ...(await Promise.all(
        children.map((child) => readEntry(child, countFile)),
      )),
    ]);
  } catch (error) {
    return failedSelection(error, relativePath);
  }
}

/** Mirrors the ZIP import: macOS metadata never reaches the vault. */
function isMetadataPath(relativePath: string): boolean {
  const segments = relativePath.split("/");
  return (
    segments[segments.length - 1] === ".DS_Store" ||
    segments.includes("__MACOSX")
  );
}

function isFileEntry(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile;
}

function isDirectoryEntry(
  entry: FileSystemEntry,
): entry is FileSystemDirectoryEntry {
  return entry.isDirectory;
}

function readFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/** Reads until an empty batch: one call returns at most 100 entries. */
async function readDirectoryEntries(
  entry: FileSystemDirectoryEntry,
  onReadFailure: (error: unknown) => void,
): Promise<FileSystemEntry[]> {
  const reader = entry.createReader();
  const readBatch = (): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => reader.readEntries(resolve, reject));

  const entries: FileSystemEntry[] = [];
  try {
    while (true) {
      const batch = await readBatch();
      if (!batch.length) {
        break;
      }
      entries.push(...batch);
    }
  } catch (error) {
    onReadFailure(error);
  }
  return entries;
}

function failedSelection(
  error: unknown,
  relativePath: string,
): UploadSelection {
  return {
    files: [],
    folderPaths: [],
    readFailures: [{ relativePath, error }],
  };
}

function mergeSelections(selections: UploadSelection[]): UploadSelection {
  const readFailures = selections.flatMap(
    (selection) => selection.readFailures ?? [],
  );
  return {
    files: selections.flatMap((selection) => selection.files),
    folderPaths: selections.flatMap((selection) => selection.folderPaths),
    ...(readFailures.length ? { readFailures } : {}),
  };
}
